import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import ProductCard from '../components/ProductCard';
import { Button } from '@/components/ui/button';
import { ChevronRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ShopPage = () => {
  const { categorySlug } = useParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [currentCategory, setCurrentCategory] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const catRes = await axios.get(`${API}/categories`);
        setCategories(catRes.data);

        let productsUrl = `${API}/products`;
        if (categorySlug) {
          const category = catRes.data.find(c => c.slug === categorySlug);
          if (category) {
            setCurrentCategory(category);
            productsUrl += `?category_id=${category.id}`;
          }
        } else {
          setCurrentCategory(null);
        }

        const prodRes = await axios.get(productsUrl);
        setProducts(prodRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [categorySlug]);

  return (
    <div className="min-h-screen">
      {/* Header */}
      <section className="bg-white py-12 md:py-16">
        <div className="container-handcraft">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
            <Link to="/" className="hover:text-charcoal transition-colors">Home</Link>
            <ChevronRight className="w-4 h-4" />
            <Link to="/shop" className="hover:text-charcoal transition-colors">Shop</Link>
            {currentCategory && (
              <>
                <ChevronRight className="w-4 h-4" />
                <span className="text-charcoal">{currentCategory.name}</span>
              </>
            )}
          </nav>

          <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4">
            <div>
              <p className="font-handwritten text-2xl text-accent mb-2">
                {currentCategory ? 'Category' : 'All Products'}
              </p>
              <h1 className="font-serif text-4xl md:text-5xl text-charcoal">
                {currentCategory ? currentCategory.name : 'Shop'}
              </h1>
              {currentCategory && (
                <p className="text-muted-foreground mt-2">{currentCategory.description}</p>
              )}
            </div>
            <p className="text-muted-foreground text-sm">
              {products.length} {products.length === 1 ? 'product' : 'products'}
            </p>
          </div>
        </div>
      </section>

      {/* Filters / Categories */}
      <section className="bg-cream border-y border-sand py-4">
        <div className="container-handcraft">
          <div className="flex items-center gap-4 overflow-x-auto pb-2 scrollbar-hide">
            <Link to="/shop">
              <Button 
                variant={!categorySlug ? 'default' : 'ghost'}
                className={`rounded-full whitespace-nowrap ${!categorySlug ? 'bg-charcoal text-cream' : ''}`}
                data-testid="filter-all"
              >
                All Products
              </Button>
            </Link>
            {categories.map(category => (
              <Link key={category.id} to={`/shop/${category.slug}`}>
                <Button 
                  variant={categorySlug === category.slug ? 'default' : 'ghost'}
                  className={`rounded-full whitespace-nowrap ${categorySlug === category.slug ? 'bg-charcoal text-cream' : ''}`}
                  data-testid={`filter-${category.slug}`}
                >
                  {category.name}
                </Button>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="section-spacing">
        <div className="container-handcraft">
          {loading ? (
            <div className="product-grid">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="space-y-4">
                  <div className="aspect-square rounded-2xl skeleton" />
                  <div className="h-6 w-3/4 skeleton rounded" />
                  <div className="h-4 w-1/2 skeleton rounded" />
                </div>
              ))}
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-20">
              <p className="font-handwritten text-3xl text-accent mb-4">Coming Soon</p>
              <h2 className="font-serif text-2xl text-charcoal mb-4">No products yet</h2>
              <p className="text-muted-foreground mb-8">
                We're working on adding products to this category.
              </p>
              <Link to="/shop">
                <Button className="btn-handcraft bg-charcoal text-cream">
                  Browse All Products
                </Button>
              </Link>
            </div>
          ) : (
            <div className="product-grid">
              {products.map((product, index) => (
                <ProductCard key={product.id} product={product} index={index} />
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default ShopPage;
